import { TypeManagerDecorator } from "../../main/type.map.service";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { Calendar, CalendarService, WidgetType } from "./calendaring.service";
import { EmbeddingUtil } from "../../util/embedding.util";

@TypeManagerDecorator("s25-ng-calendaring")
@Component({
    selector: "s25-ng-calendaring",
    template: `
        @if (init) {
            @switch (widgetType) {
                @case (WidgetType.table) {
                    <s25-ng-calendar-table
                        [calendar]="calendar"
                        [startDt]="startDt"
                        [endDt]="endDt"
                    ></s25-ng-calendar-table>
                }
                @default {
                    <div>Unknown calendar type: [{{ widgetType }}]</div>
                }
            }
        }
    `,
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CalendaringComponent implements OnInit {
    constructor(
        private cd: ChangeDetectorRef,
        private elementRef: ElementRef,
    ) {}

    // one of:
    @Input() calendarId?: number; // fetch calendar by the PK: calendarId
    @Input() webName?: string; // fetch calendar by a unique key: webName
    @Input() widgetType?: WidgetType;
    @Input() startDt?: Date;
    @Input() endDt?: Date;
    @Input() selectedSubcalendars?: string;

    @Input() calendar?: Calendar; // useful for easy testing

    init = false;

    async ngOnInit() {
        const urlSearchParams = new URLSearchParams(window.location.search);

        if (!this.calendar && !this.calendarId && !this.webName) {
            this.calendarId = parseInt(urlSearchParams.get("calendarId")) || undefined;
            this.webName = urlSearchParams.get("webName");

            // if the webName is in the format of calendar.123, then we can extract the calendarId
            if (this.webName && /^calendar\.[0-9]+$/.test(this.webName)) {
                this.calendarId = parseInt(this.webName.split(".")[1]);
                this.webName = undefined;
            }
        }

        if (!this.startDt && urlSearchParams.get("startDt")) {
            this.startDt = new Date(urlSearchParams.get("startDt"));
        }

        if (!this.endDt && urlSearchParams.get("endDt")) {
            this.endDt = new Date(urlSearchParams.get("endDt"));
        }

        if (!this.widgetType && urlSearchParams.get("widgetType")) {
            this.widgetType = urlSearchParams.get("widgetType") as WidgetType;
        }

        if (!this.selectedSubcalendars && urlSearchParams.get("selectedSubcalendars")) {
            this.selectedSubcalendars = urlSearchParams.get("selectedSubcalendars");
        }

        let calendarPromise: Promise<Calendar> = Promise.resolve(this.calendar);

        if (!this.calendar && this.calendarId) {
            // get calendar by id
            calendarPromise = CalendarService.getCalendarById(this.calendarId);
        } else if (!this.calendar && this.webName) {
            // get calendar by webName
            calendarPromise = CalendarService.getCalendarByWebName(this.webName);
        }

        this.calendar = await calendarPromise;
        if (this.calendar) {
            if (!this.widgetType || this.widgetType === WidgetType.main) {
                this.widgetType = this.calendar.defaultWidget;
            }

            this.calendar.subcalendars ??= [];
            this.calendar.subcalendars.sort((a, b) => {
                const aStr = a.displayName?.toLowerCase() ?? "";
                const bStr = b.displayName?.toLowerCase() ?? "";
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            });

            if (this.selectedSubcalendars) {
                // select the subcalendars that are in the selectedSubcalendars list
                const selectedIds = this.selectedSubcalendars
                    .split(",")
                    .filter((id) => !!id)
                    .map((id) => parseInt(id));
                this.calendar.subcalendars.forEach((sc) => (sc.selected = selectedIds.includes(sc.calendarId)));
            }

            if (!this.calendar.subcalendars.find((sc) => sc.selected)) {
                // if none are selected, select all
                this.calendar.subcalendars.forEach((sc) => (sc.selected = true));
            }

            this.init = true;
            this.cd.detectChanges();

            EmbeddingUtil.getSingleton().registerHeightManager(this.elementRef.nativeElement, null, 120, 1000);
        }
    }

    protected readonly WidgetType = WidgetType;
}
